import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/LoggingService';
import { fetchAllAllocations, insertAllocationsBulk } from '@/services/portfolio/PortfolioAllocationsService';

export async function createPortfolio({ userId, name, portfolioType, portfolioSize, status }: { userId: string; name: string; portfolioType: string; portfolioSize?: number; status?: string }): Promise<string> {
  const payload: any = { name, user_id: userId, portfolio_type: portfolioType };
  if (typeof portfolioSize === 'number') payload.portfolio_size = portfolioSize;
  if (typeof status === 'string') payload.status = status;
  const { data, error } = await supabase
    .from('portfolios')
    .insert(payload)
    .select('id')
    .single();

  if (error || !data?.id) {
    logger.error('PortfolioService', 'createPortfolio failed', { error });
    throw error || new Error('Failed to create portfolio');
  }
  return data.id;
}

/**
 * Convert a personal/client portfolio with $ allocation values into a new Model portfolio
 * with percentage allocations (2 decimals), default size = 100,000. Starts clean (no notes/tags copied).
 * Returns the new portfolio ID.
 */
export async function convertPortfolioToModel(params: { originalId: string; userId: string }): Promise<string> {
  const { originalId, userId } = params;
  // Fetch original portfolio
  const original = await getPortfolioById(originalId);
  if (!original) {
    throw new Error('Original portfolio not found');
  }
  const origType = String(original.portfolio_type || '').toLowerCase();
  if (origType === 'model') {
    throw new Error('Cannot convert a model portfolio');
  }

  // Fetch allocations
  const allocations = await fetchAllAllocations(originalId);
  const values = allocations
    .map((a: any) => Number(a?.allocation_value) || 0)
    .filter((v: number) => v > 0);
  const total = values.reduce((sum: number, v: number) => sum + v, 0);
  if (!Number.isFinite(total) || total <= 0) {
    throw new Error('Portfolio has no dollar allocations to convert');
  }

  // Create new model portfolio with default size
  const newId = await createPortfolio({
    userId,
    name: `${original.name} (Model)`,
    portfolioType: 'model',
    portfolioSize: 100000,
    status: 'in_progress',
  });

  // Build percentage allocations from stored percentage or by value/original portfolio_size (fallback to sum of values)
  const portfolioSizeNum = Number(original.portfolio_size) || 0;
  const denom = portfolioSizeNum > 0 ? portfolioSizeNum : total;
  const base = allocations
    .map((a: any) => {
      const pctStored = Number(a?.allocation_percentage) || 0;
      const val = Number(a?.allocation_value) || 0;
      let pct = pctStored;
      if (pct <= 0 && val > 0 && denom > 0) {
        pct = (val / denom) * 100;
      }
      return {
        asset_id: a.asset_id,
        asset_type: a.asset_type,
        pct,
        value: val,
      };
    })
    .filter((x) => (x.pct || 0) > 0);

  const roundedPercents = base.map((item) => Math.round(item.pct * 100) / 100);

  // Normalize rounding residual to 100.00 if we're close
  const sumRounded = roundedPercents.reduce((s, v) => s + v, 0);
  const nearHundred = Math.abs(sumRounded - 100) <= 0.5; // allow rounding slack
  if (nearHundred && roundedPercents.length > 0) {
    const residual = Math.round((100 - sumRounded) * 100) / 100;
    if (Math.abs(residual) >= 0.01) {
      const maxIndex = base.reduce((idx, cur, i, arr) => (cur.value > arr[idx].value ? i : idx), 0);
      roundedPercents[maxIndex] = Math.round((roundedPercents[maxIndex] + residual) * 100) / 100;
    }
  }

  const modelSize = 100000; // must match createPortfolio above
  const payloads = base.map((item, i) => ({
    portfolio_id: newId,
    asset_id: item.asset_id,
    asset_type: item.asset_type,
    allocation_percentage: roundedPercents[i],
    allocation_value: Math.round((modelSize * (roundedPercents[i] / 100)) * 100) / 100,
    is_value_locked: false as any,
  } as any));

  if (payloads.length > 0) {
    await insertAllocationsBulk(payloads as any);
  }

  return newId;
}

export async function getPortfolioById(id: string): Promise<any | null> {
  const { data, error } = await supabase
    .from('portfolios')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) {
    logger.error('PortfolioService', 'getPortfolioById failed', { error, id });
    throw error;
  }
  return data || null;
}

export async function getIsSizeLocked(id: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('portfolios')
    .select('is_size_locked')
    .eq('id', id)
    .maybeSingle();
  if (error) {
    logger.warn('PortfolioService', 'getIsSizeLocked failed', { error, id });
    return false;
  }
  return !!(data as any)?.is_size_locked;
}

export async function updatePortfolioName(id: string, name: string): Promise<void> {
  const { error } = await supabase
    .from('portfolios')
    .update({ name })
    .eq('id', id);
  if (error) {
    logger.error('PortfolioService', 'updatePortfolioName failed', { error, id });
    throw error;
  }
}

export async function deletePortfolio(id: string): Promise<void> {
  const { error } = await supabase
    .from('portfolios')
    .delete()
    .eq('id', id);
  if (error) {
    logger.error('PortfolioService', 'deletePortfolio failed', { error, id });
    throw error;
  }
}

export async function deletePortfoliosBulk(ids: string[]): Promise<void> {
  const { error } = await supabase
    .from('portfolios')
    .delete()
    .in('id', ids);
  if (error) {
    logger.error('PortfolioService', 'deletePortfoliosBulk failed', { error, ids });
    throw error;
  }
}

export async function getPortfolioSize(id: string): Promise<number | null> {
  const { data, error } = await supabase
    .from('portfolios')
    .select('portfolio_size')
    .eq('id', id)
    .maybeSingle();
  if (error) return null;
  const size = (data as any)?.portfolio_size;
  return Number.isFinite(Number(size)) ? Number(size) : null;
}

export async function getPortfolioType(id: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('portfolios')
    .select('portfolio_type')
    .eq('id', id)
    .maybeSingle();
  if (error) return null;
  const t = (data as any)?.portfolio_type;
  return typeof t === 'string' && t ? t : null;
}

export async function updatePortfolioFields(
  id: string,
  fields: Partial<{ name: string; status: string; updated_at: string; portfolio_size: number; portfolio_type: string }>
): Promise<void> {
  const { error } = await supabase
    .from('portfolios')
    .update(fields as any)
    .eq('id', id);
  if (error) {
    logger.error('PortfolioService', 'updatePortfolioFields failed', { error, id, fields });
    throw error;
  }
}
